import {
  CreatedFields,
  DeletedFields,
  DynamicSchema,
  ProfileFields,
  SchemaWithId,
} from '@soniq/schema'
import {
  EntityReadStore,
  EntityWriteStore,
} from '../EntityStore'
import {
  ProfileProjectionStoreDb,
  ProjectionEntityStoreDb,
} from './types'
import {
  ValidateSchemaFunction,
  ValidateSchemaOptions,
} from '@pogokid/schema'
import {
  OrderByOptions,
  ResourceRequestOptions,
  UpsertSchema,
} from '../types'
import { QueryResponse, SingleResponse } from '../Response'
import { castValues } from '@pogokid/dblite'
import { normaliseIsoDateTimeString } from '@pogokid/util/datetime'
import { pick } from '@pogokid/util/lodash'
import { isArrayWithLength } from '@pogokid/util'

export class ProjectionEntityStore<
  Schema extends SchemaWithId<
    CreatedFields & DeletedFields & ProfileFields
  >
> implements EntityReadStore<Schema>, EntityWriteStore<Schema>
{
  protected db: ProjectionEntityStoreDb | undefined
  readonly validate: ValidateSchemaFunction<Schema>
  protected get updateExcludedKeys(): ReadonlyArray<string> {
    return ['id', 'deleted_at', 'deleted_by']
  }

  constructor(
    public entity: string,
    public schema: DynamicSchema<Schema>
  ) {
    this.validate = schema.validateFn
  }

  protected getDb({
    db,
  }: ResourceRequestOptions): ProjectionEntityStoreDb {
    if (!db) {
      throw new Error('Local database connection required')
    }
    return db as unknown as ProjectionEntityStoreDb
  }

  protected getProfileDb(
    options: ResourceRequestOptions
  ): ProfileProjectionStoreDb {
    return this.getDb(
      options
    ) as unknown as ProfileProjectionStoreDb
  }

  isValidFilter(filter: unknown): filter is Partial<Schema> {
    return this.schema.isPartial(filter)
  }

  /**
   * Map each row from query data into the Schema.
   *
   * This is useful if you need to manipulate the row
   * data before returning.
   *
   * @param data
   * @protected
   */
  normaliseRecordData<
    Returns extends SchemaWithId<Schema> = SchemaWithId<Schema>
  >(data: Returns): Returns {
    if (data) {
      const booleanProperties = this.schema
        .propertiesOfType('boolean')
        .reduce((acc, property) => {
          const value =
            property.name in data
              ? ((data as Record<string, unknown>)[
                  property.name
                ] as unknown)
              : null
          if (value === null || value === undefined) return acc
          return {
            ...acc,
            [property.name]: Boolean(value),
          }
        }, {})
      return {
        ...data,
        ...booleanProperties,
        created_at: normaliseIsoDateTimeString(data.created_at),
      }
    }
    return data
  }

  async create(
    options: ResourceRequestOptions,
    profileId: string,
    userId: string,
    data: Schema
  ): Promise<SingleResponse<SchemaWithId<Schema>>> {
    const validateResult = this.validateToResponse(
      profileId,
      data
    )
    if (!validateResult.error && validateResult.data) {
      await this.getDb(options)
        .insertInto(this.entity)
        .values(castValues<Schema>(validateResult.data))
        // No conflict target, so this covers the table's dedupe
        // anchors as well as its id. Two events can describe one fact
        // — two devices doing the same thing — and each carries its
        // own row id, so the id alone cannot recognise them as the
        // same. Naming `id` here would let the second one reach the
        // anchor's unique index and throw, which stalls the
        // projection rather than skipping the row.
        .onConflict((oc) => oc.doNothing())
        .execute()
    }

    return validateResult
  }

  fetchByIdQuery(
    options: ResourceRequestOptions,
    profileId: string,
    id: string
  ) {
    return this.getDb(options)
      .selectFrom(this.entity)
      .selectAll()
      .where('profile_id', '=', profileId)
      .where('id', '=', id)
      .limit(1)
  }

  async fetchById(
    options: ResourceRequestOptions,
    profileId: string,
    id: string
  ): Promise<SingleResponse<SchemaWithId<Schema>>> {
    const data = (await this.fetchByIdQuery(
      options,
      profileId,
      id
    ).executeTakeFirst()) as SchemaWithId<Schema>

    return { data: data ? this.normaliseRecordData(data) : null }
  }

  fetchByIdsQuery(
    options: ResourceRequestOptions,
    profileId: string,
    ids: string[]
  ) {
    return this.getDb(options)
      .selectFrom(this.entity)
      .selectAll()
      .where('profile_id', '=', profileId)
      .where('id', 'in', ids.toSorted())
  }

  async fetchByIds(
    options: ResourceRequestOptions,
    profileId: string,
    ids: string[]
  ): Promise<QueryResponse<SchemaWithId<Schema>>> {
    const data = (await this.fetchByIdsQuery(
      options,
      profileId,
      ids
    ).execute()) as SchemaWithId<Schema>[]

    return {
      data: data
        ? data.map((row) => this.normaliseRecordData(row))
        : [],
    }
  }

  fetchQueryQuery(
    options: ResourceRequestOptions,
    profileId: string,
    filters?: Partial<Schema>,
    _order?: Partial<Record<keyof Schema, OrderByOptions>>
  ) {
    let query = this.getDb(options)
      .selectFrom(this.entity)
      .selectAll()
      .where('profile_id', '=', profileId)
      .where('deleted_at', 'is', null)

    if (this.isValidFilter(filters)) {
      query = query.where((eb) => eb.and(filters ?? {}))
    }

    return query
  }

  async fetchQuery(
    options: ResourceRequestOptions,
    profileId: string,
    filters?: Partial<Schema>,
    order?: Partial<Record<keyof Schema, OrderByOptions>>
  ): Promise<QueryResponse<SchemaWithId<Schema>>> {
    const data = (await this.fetchQueryQuery(
      options,
      profileId,
      filters,
      order
    ).execute()) as SchemaWithId<Schema>[]

    return {
      data: data
        ? data.map((row) => this.normaliseRecordData(row))
        : [],
    }
  }

  async update(
    options: ResourceRequestOptions,
    profileId: string,
    data: SchemaWithId<Partial<Schema>>
  ): Promise<SingleResponse<Schema>> {
    const validateResult = this.validateToResponse(
      profileId,
      data,
      { allowPartial: true }
    )
    if (!validateResult.error && validateResult.data) {
      const updatedKeys = Object.keys(data).filter(
        (k) => !this.updateExcludedKeys.includes(k)
      ) as (keyof Schema)[]
      const updateData = pick(updatedKeys, validateResult.data)
      if (isArrayWithLength(updatedKeys)) {
        await this.getDb(options)
          .updateTable(this.entity)
          .set(castValues(updateData))
          .where('id', '=', data.id)
          .execute()
      }
    }

    return validateResult
  }

  async upsert(
    options: ResourceRequestOptions,
    profileId: string,
    data: UpsertSchema<Schema>[]
  ): Promise<QueryResponse<SchemaWithId<Schema>>> {
    if (!profileId) {
      throw new Error('Profile is required')
    }
    const { isValid, errors, cleanedValues } =
      this.schema.validateMany(data)
    if (!isValid) {
      throw new Error(
        `Validation failed: ${JSON.stringify(errors, null, 2)}`
      )
    }
    if (cleanedValues.length > 0) {
      const db = this.getDb(options)
      for (const data of cleanedValues) {
        const values = castValues(data)
        const existing = await db
          .selectFrom(this.entity)
          .select('id')
          .where('id', '=', values.id)
          .executeTakeFirst()
        if (existing) {
          const {
            id,
            created_at,
            created_by,
            deleted_by,
            deleted_at,
            profile_id,
            ...updateSet
          } = values
          await db
            .updateTable(this.entity)
            .set(updateSet)
            .where('id', '=', id)
            .execute()
        } else {
          await db
            .insertInto(this.entity)
            .values(values)
            // The id is new, but a dedupe anchor on the table may not
            // be — see the note in `create`.
            .onConflict((oc) => oc.doNothing())
            .execute()
        }
      }
    }

    return { data: cleanedValues }
  }

  async delete(
    options: ResourceRequestOptions,
    profileId: string,
    deletedBy: string,
    ids: string[]
  ): Promise<void> {
    await this.getDb(options)
      .updateTable(this.entity)
      .set({
        deleted_at: new Date().toISOString(),
        deleted_by: deletedBy,
      })
      .where('id', 'in', ids)
      .where('profile_id', '=', profileId)
      .execute()
  }

  validateToResponse(
    profileId: string,
    data: unknown,
    options?: ValidateSchemaOptions
  ): SingleResponse<SchemaWithId<Schema>> {
    if (!profileId) {
      throw new Error('Profile is required')
    }
    const { isValid, cleanedValues, errors } = this.validate(
      data,
      options
    )
    if (!isValid) {
      return {
        data: null,
        error: new Error(
          `Validation failed: ${JSON.stringify(errors, null, 2)}`
        ),
      }
    } else {
      return {
        data: cleanedValues,
      }
    }
  }
}
